import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { PlayCircle } from "lucide-react"
import LocationCards, { type LocationCard } from "@/components/LocationCards"
import { HeroFilm, type HeroPhoto } from "@/components/gisela/HeroBand"
import { bandBg } from "@/components/gisela/TextBand"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { GiselaTherapist, PageMeta, PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

/** A location's tour, as the location pages and the home band play it. */
interface TourVideo {
  title: string
  embed_src: string | null
  file_url: string | null
  poster_url?: string | null
}

interface Photo {
  id: number
  url: string | null
  alt_text: string | null
}

interface Props {
  therapist: GiselaTherapist | null
  sites: LocationCard[]
  content: {
    hero: { heading: string; subheading: string; photo?: HeroPhoto | null; _hidden?: boolean }
    who_we_are: { body: string; _hidden?: boolean }
    /** Drawn inside the Our Team band, above Gisela's card. */
    community: { heading?: string; body?: string; _hidden?: boolean }
    /** Her card's background: the photo chosen in its own slot, under the film
     *  that slot picked — as a location page's "Therapy with Gisela" band. */
    gisela_card: { photo?: HeroPhoto | null }
    building_history: {
      body: string
      photo?: Photo
      /** A location's tour, when the section's "Video tour" setting names one.
       *  The photo above is the still it rests on. */
      video?: TourVideo | null
      _hidden?: boolean
    }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

export default function About({ therapist, sites, content, section_order, meta }: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.about
  const tc = translations.sanctuary_pages.common

  // Someone who landed here from a search should see the building without a
  // second click, so the tour plays where the photograph would have been.
  const tour = content.building_history.video
  const buildingTour = tour && (tour.embed_src || tour.file_url) ? tour : null
  // The band's own photograph is the still; the video library's poster stands
  // in when this band has no picture of its own.
  const buildingStill = content.building_history.photo?.url ?? tour?.poster_url ?? null

  const community = content.community
  const hasCommunity = !sectionHidden(community) && !!(community?.heading || community?.body)
  const title = meta?.title ?? "About Sanctuary Therapy Centre | Prestwich | Est. 1836"
  const description =
    meta?.description ??
    "About Sanctuary Therapy Centre — home to Gisela Ramirez's online and in-person therapy practice, and therapy rooms in Prestwich and Hebden Bridge since 1836."

  const bands = presentBands(
    [
      {
        id: "about-who-we-are",
        present: !sectionHidden(content.who_we_are),
        render: (i) => (
                  <section className={`relative overflow-hidden ${bandBg(i)} py-16`}>
                    <BrandMotif variant="speckle" className="absolute -top-16 right-10" />
                    <div className="relative mx-auto max-w-3xl px-6 lg:px-8">
                      <h2>{t.who_we_are}</h2>
                      <div className="mt-5 text-ink-muted [&_p]:mt-4 [&_p:first-child]:mt-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.who_we_are.body}</ReactMarkdown>
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: "about-building-history",
        present: !sectionHidden(content.building_history),
        render: (i) => (
                  <section className={`${bandBg(i)} py-16`}>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
                        <div>
                          <p className="mb-2 text-xs font-medium uppercase tracking-widest text-ink-muted">
                            {tc.est_1836}
                          </p>
                          <h2>{t.building_heading}</h2>
                          <div className="mt-5 text-ink-muted [&_p]:mt-4 [&_p:first-child]:mt-0">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.building_history.body}</ReactMarkdown>
                          </div>
                        </div>

                        {buildingTour ? (
                          <div>
                            <p className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-ink-muted">
                              <PlayCircle aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                              {tc.take_a_tour}
                            </p>
                            <div className="aspect-video overflow-hidden rounded-2xl bg-stone-mid ring-1 ring-hairline">
                              {buildingTour.embed_src ? (
                                <iframe
                                  src={buildingTour.embed_src}
                                  title={buildingTour.title}
                                  className="h-full w-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  allowFullScreen
                                  loading="lazy"
                                  referrerPolicy="strict-origin-when-cross-origin"
                                />
                              ) : (
                                <video
                                  // See HomeBuildingHistory: #t=0.1 only when
                                  // there is no still, so the seek can't
                                  // replace a chosen one.
                                  src={buildingStill ? buildingTour.file_url! : `${buildingTour.file_url!}#t=0.1`}
                                  poster={buildingStill ?? undefined}
                                  title={buildingTour.title}
                                  controls
                                  preload="metadata"
                                  className="h-full w-full object-cover"
                                />
                              )}
                            </div>
                          </div>
                        ) : content.building_history.photo?.url ? (
                          <img
                            src={content.building_history.photo.url}
                            alt={content.building_history.photo.alt_text ?? ""}
                            className="h-72 w-full rounded-2xl object-cover ring-1 ring-hairline"
                          />
                        ) : (
                          <div className="flex h-72 items-center justify-center rounded-2xl bg-stone-mid ring-1 ring-hairline">
                            <span className="text-sm text-ink-muted">{tc.building_photo_coming_soon}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: "about-team",
        present: !!therapist || hasCommunity,
        render: (i) => (
                  <section className={`${bandBg(i)} py-16`}>
                    <div className="mx-auto max-w-3xl px-6 lg:px-8">
                      <h2>{t.our_team}</h2>

                      {/* The practitioners who work here, before the one whose
                          practice the building is home to. */}
                      {hasCommunity && (
                        <div className="mt-5">
                          {content.community.heading && <h3>{content.community.heading}</h3>}
                          {content.community.body && (
                            <div className="mt-3 text-ink-muted [&_p]:mt-4 [&_p:first-child]:mt-0">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.community.body}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      )}

                      {/* The blue card the venue's home introduces her with,
                          and the same white pill for its way in. */}
                      {therapist && (
                      <div className="relative mt-8 overflow-hidden rounded-2xl bg-teal text-white">
                        {/* The photo placed in the card's own slot, under the
                            film it chose — the plain blue when none is. */}
                        <HeroFilm photo={content.gisela_card?.photo} />
                        <div className="relative flex flex-col gap-5 p-8 sm:flex-row sm:items-start lg:p-10">
                          {therapist.photo_url ? (
                            <img
                              src={therapist.photo_url}
                              alt={therapist.name}
                              className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-white/40"
                            />
                          ) : (
                            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/10 ring-2 ring-white/40">
                              <span className="font-medium text-white/80">
                                {therapist.name.split(" ").map((n) => n[0]).join("")}
                              </span>
                            </div>
                          )}
                          <div>
                            <h3 className="text-white">{therapist.name}</h3>
                            {therapist.speciality_en && (
                              <p className="mt-0.5 text-sm text-white">{therapist.speciality_en}</p>
                            )}
                            {therapist.bio_en && (
                              // Markdown, like every other body on the site —
                              // the bold themes in her bio were reaching the
                              // page as asterisks. The paragraphs are coloured
                              // one by one: a bare <p> takes its colour from
                              // the base layer, which beats an inherited class
                              // on the wrapper, so her bio was ink-dark on the
                              // blue long before a photograph went behind it.
                              <div className="mt-3 [&_a]:text-white [&_a]:underline [&_p]:mt-3 [&_p]:text-white [&_p:first-child]:mt-0 [&_strong]:font-medium [&_strong]:text-white">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{therapist.bio_en}</ReactMarkdown>
                              </div>
                            )}
                            <a
                              href="/therapy-with-gisela"
                              className="mt-6 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                            >
                              {t.learn_more_therapy.replace("%{name}", therapist.name.split(" ")[0])}
                            </a>
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  </section>
        ),
      },
      {
        id: "about-locations",
        present: true,
        render: (i) => (
                <section className={`${bandBg(i)} py-16`}>
                  <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <h2>{t.our_locations}</h2>
                    {/* The same grid the home page and Rent a Room show, so a
                        location is introduced by its building everywhere. */}
                    <div className="mt-8">
                      <LocationCards sites={sites} />
                    </div>
                  </div>
                </section>
        ),
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={
            meta?.description ??
            "A Victorian building in the heart of Prestwich and a second location in Hebden Bridge — calm, professional spaces for therapy since 1836."
          }
        />
      </Head>
      <PublicLayout>
        {/* ── Hero ───────────────────────────────────────────────────── */}
        {!sectionHidden(content.hero) && (
          <section className="relative overflow-hidden bg-teal pt-32 pb-16 text-white">
            {/* The chosen photograph under the band's own blue, so the white
                copy reads on any picture. The motif is the plain band's
                flourish — a photo is flourish enough. */}
            <HeroFilm photo={content.hero.photo} />
            {!content.hero.photo?.url && (
              <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            )}
            <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
                {tc.est_1836}
              </p>
              <h1 className="text-white">{content.hero.heading}</h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">
                {content.hero.subheading}
              </p>
            </div>
          </section>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}
      </PublicLayout>
    </>
  )
}
